import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Copy, CheckCircle2, XCircle, ChevronDown } from "lucide-react";
import { SECURITY_HEADERS, CLOUDFLARE_STEPS } from "@/lib/remediations";
import { CopyBox } from "@/components/FixGuide";

export default function SecurityHeadersFix({ security }) {
  const [open, setOpen] = useState(false);
  const sec = security || {};
  // When the site blocked our scanner we could NOT read headers — never report them as
  // "missing" (that would be a false negative). Show an honest "could not determine" state.
  const headersUnknown = sec.headersRead === false;
  const rows = SECURITY_HEADERS.map((h) => ({ ...h, present: Boolean(sec[h.key]) }));
  const missing = rows.filter((r) => !r.present);
  const score = rows.filter((r) => r.present).reduce((a, r) => a + r.points, 0);
  const allBlock = missing.map((m) => `${m.name}: ${m.value}`).join("\n");

  const copyAll = () => { navigator.clipboard?.writeText(allBlock); toast.success("All missing headers copied"); };

  if (headersUnknown) {
    return (
      <div className="bg-[#121212] border border-[#FFD60A]/30 rounded-xl p-5 mb-6" data-testid="security-headers-fix">
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck size={16} className="text-[#FFD60A]" />
          <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Security Headers</h3>
        </div>
        <p className="text-[#FFD60A] text-sm mt-2 flex items-start gap-2" data-testid="headers-not-assessed">
          <XCircle size={15} className="mt-0.5 shrink-0" />
          <span>Could not determine — the site (or its CDN/firewall) blocked our scanner from reading the page, so we can't confirm these headers <b>either way</b>. This is <b>not</b> a "missing headers" result. Re-run the scan, or check from a different network; if it keeps blocking, allowlist our scanner.</span>
        </p>
        <p className="text-zinc-600 text-[11px] mt-3">Note: security headers are read from a direct HTTPS request to the site — the same values a real browser receives. A CDN can't hide them from us unless it blocks the request entirely (which is what happened here).</p>
      </div>
    );
  }

  return (
    <div className="bg-[#121212] border border-[#4da3ff]/25 rounded-xl p-5 mb-6" data-testid="security-headers-fix">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[#4da3ff]" />
          <h3 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Security Headers — Fix to 100</h3>
        </div>
        <span className="font-mono text-xs" style={{ color: score >= 90 ? "#32D74B" : score >= 60 ? "#FFD60A" : "#FF7A45" }} data-testid="headers-score">
          {score}/100 from headers
        </span>
      </div>

      {missing.length === 0 ? (
        <p className="text-[#32D74B] text-sm mt-2 flex items-center gap-2" data-testid="headers-all-set">
          <CheckCircle2 size={15} /> All recommended security headers are present. 
        </p>
      ) : (
        <>
          <p className="text-zinc-500 text-sm mb-3">Add the missing headers below to your site's responses to close the gap. Each shows the exact value to set.</p>
          <div className="space-y-2">
            {rows.map((r) => (
              <div key={r.key} className="flex items-start gap-2 rounded-lg border border-white/10 bg-black/25 p-2.5" data-testid={`header-row-${r.key}`}>
                {r.present
                  ? <CheckCircle2 size={15} className="text-[#32D74B] mt-0.5 shrink-0" />
                  : <XCircle size={15} className="text-[#FF7A45] mt-0.5 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-xs font-semibold font-mono">{r.name}</span>
                    <span className="text-[10px] text-zinc-500">+{r.points} pts</span>
                    {r.present && <span className="text-[10px] text-[#32D74B]">present</span>}
                  </div>
                  {!r.present && (
                    <div className="relative mt-1">
                      <pre className="bg-black/60 border border-white/10 rounded p-2 pr-8 text-[10.5px] text-[#8fe3ff] font-mono overflow-x-auto whitespace-pre-wrap break-all">{r.value}</pre>
                      <button
                        onClick={() => { navigator.clipboard?.writeText(`${r.name}: ${r.value}`); toast.success(`${r.name} copied`); }}
                        data-testid={`copy-header-${r.key}`}
                        className="absolute top-1.5 right-1.5 text-zinc-400 hover:text-white" title="Copy header">
                        <Copy size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            <button onClick={copyAll} data-testid="copy-all-headers-btn" className="inline-flex items-center gap-1.5 btn-brand text-white rounded-lg px-4 py-2 text-xs font-medium">
              <Copy size={13} /> Copy all {missing.length} missing header{missing.length > 1 ? "s" : ""}
            </button>
            <button onClick={() => setOpen((o) => !o)} data-testid="headers-steps-toggle" className="inline-flex items-center gap-1.5 bg-white/5 border border-white/15 hover:bg-white/10 text-white rounded-lg px-4 py-2 text-xs transition-colors">
              Where do I add these? <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          </div>

          {open && (
            <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-3" data-testid="headers-steps">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Add via Cloudflare (free plan) — or at your server/CDN</div>
              <ol className="list-decimal list-inside space-y-1 text-xs text-zinc-300">
                {CLOUDFLARE_STEPS.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
              </ol>
              <CopyBox code={allBlock} label="All missing headers (name: value per line)" />
            </div>
          )}
        </>
      )}
      <p className="text-zinc-600 text-[11px] mt-3">Tip: these headers must be sent on the site's HTML responses (root document), not just API responses.</p>
    </div>
  );
}
